import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useImportVoluntaryEmail } from "../../lib/queries/voluntaryEmails";
import { PreparationCabinetGate } from "../quote-requests/_parts/PreparationCabinetGate";

export function VoluntaryEmailUploadPage() {
  return (
    <PreparationCabinetGate>
      <EmailUpload />
    </PreparationCabinetGate>
  );
}
function EmailUpload() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const upload = useImportVoluntaryEmail();
  const [selection, setSelection] = useState<{ file: File; key: string } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const submitting = useRef(false);
  const error = invalid
    ? t("voluntary_email.invalid_file")
    : upload.error
      ? getApiErrorMessage(upload.error, normalizeLang(i18n.resolvedLanguage))
      : null;
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/intake-events"
        className="focus-visible:ring-brand-500 underline focus-visible:ring-2"
      >
        {t("reception_flow.receipts")}
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">{t("voluntary_email.upload")}</h1>
      <p className="text-slate-700">{t("voluntary_email.intro")}</p>
      <form
        className="space-y-5 rounded-xl border border-slate-200 bg-white p-5"
        aria-busy={upload.isPending}
        onSubmit={async (event) => {
          event.preventDefault();
          if (!selection || submitting.current) return;
          submitting.current = true;
          try {
            const result = await upload.mutateAsync(selection);
            navigate(`/intake-events/${result.intake_event_id}`);
          } catch {
            requestAnimationFrame(() => errorRef.current?.focus());
          } finally {
            submitting.current = false;
          }
        }}
      >
        <label className="block font-medium text-slate-900" htmlFor="email-file">
          {t("voluntary_email.file")}
        </label>
        <p id="email-file-help" className="text-sm text-slate-600">
          {t("voluntary_email.file_help")}
        </p>
        <input
          id="email-file"
          data-guide-target="email-file"
          type="file"
          accept=".eml,message/rfc822"
          disabled={upload.isPending}
          aria-describedby="email-file-help"
          aria-invalid={invalid}
          className="focus-visible:ring-brand-500 min-h-11 w-full rounded border border-slate-300 p-2 focus-visible:ring-2"
          onChange={(event) => {
            upload.reset();
            const file = event.target.files?.[0];
            const valid =
              !!file && /\.eml$/i.test(file.name) && file.size > 0 && file.size <= 10 * 1024 * 1024;
            setInvalid(!!file && !valid);
            setSelection(valid ? { file, key: crypto.randomUUID() } : null);
          }}
        />
        {error && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            className="rounded border border-red-200 bg-red-50 p-3 text-red-800"
          >
            {error}
          </div>
        )}
        <p className="text-sm text-slate-600">{t("voluntary_email.retry_help")}</p>
        <button
          type="submit"
          disabled={!selection || upload.isPending}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 min-h-11 rounded px-4 py-2 font-medium text-white focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          {t(upload.isPending ? "voluntary_email.importing" : "voluntary_email.submit")}
        </button>
      </form>
    </div>
  );
}
